import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Effect, Schema } from 'effect';
import { PublicDocPromotion } from './public-doc-promotion';
import {
  InternalServerError,
  NotFoundError,
  ServiceUnavailableError,
} from './errors';

// Shared blog post summary used in list and related posts
export const BlogPostSummary = Schema.Struct({
  slug: Schema.String,
  isSticky: Schema.Boolean,
  author: Schema.String,
  authorImage: Schema.NullOr(Schema.String),
  heroImage: Schema.NullOr(Schema.String),
  heroImageAlt: Schema.NullOr(Schema.String),
  pubDate: Schema.String, // ISO date string
  tags: Schema.Array(Schema.String),
  description: Schema.String,
  title: Schema.String,
  excerpt: Schema.String,
  seoDescription: Schema.String,
});

const GetPostResponse = Schema.Struct({
  detailPage: Schema.Struct({
    htmlParts: Schema.Array(Schema.String),
    banners: Schema.Array(Schema.String),
    oldPostFormat: BlogPostSummary,
  }),
  randomPosts: Schema.Array(BlogPostSummary),
  randomDocs: Schema.Array(PublicDocPromotion),
});

const getPostEndpoint = HttpApiEndpoint.get('GetPost', '/posts/:slug', {
  params: Schema.Struct({ slug: Schema.String }),
  success: GetPostResponse.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, InternalServerError, ServiceUnavailableError],
})
  .annotate(OpenApi.Summary, 'Get Blog Post')
  .annotate(
    OpenApi.Description,
    'Fetch a single blog post by slug with processed HTML, related posts, and doc promotions',
  );

const ListPostsUrlParams = Schema.Struct({
  page: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.withDecodingDefaultType(Effect.succeed(1)),
  ),
  size: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.withDecodingDefaultType(Effect.succeed(15)),
  ),
});

const ListPostsResponse = Schema.Struct({
  posts: Schema.Array(BlogPostSummary),
  pageInfo: Schema.Struct({
    hasNextPage: Schema.Boolean,
    hasPreviousPage: Schema.Boolean,
    totalPages: Schema.Int,
    currentPage: Schema.Int,
    nextPage: Schema.NullOr(Schema.Int),
    previousPage: Schema.NullOr(Schema.Int),
    total: Schema.Int,
  }),
});

const listPostsEndpoint = HttpApiEndpoint.get('ListPosts', '/posts', {
  query: ListPostsUrlParams,
  success: ListPostsResponse.pipe(HttpApiSchema.status(200)),
  error: [InternalServerError, ServiceUnavailableError],
})
  .annotate(OpenApi.Summary, 'List Blog Posts')
  .annotate(
    OpenApi.Description,
    'Fetch paginated blog posts with offset pagination',
  );

export const blogGroup = HttpApiGroup.make('Blog')
  .add(getPostEndpoint)
  .add(listPostsEndpoint)
  .prefix('/blog');
